/**
 * Key — a tonal context (tonic + mode quality).
 *
 * The Key is the bridge between absolute pitch and movable-do thinking.
 * It tells the engine "we are in C major" or "we are in F# minor" so that
 * scale degrees, solfège, and Roman numeral analysis can all be computed.
 */

import { type Pitch, pitchToString } from "./pitch.js";
import { type Scale, scale } from "./scale.js";
import { intervalBetween } from "./interval.js";

export type KeyMode = "major" | "minor";

export interface Key {
  tonic: Pitch;
  mode: KeyMode;
  /** The diatonic scale of this key (natural minor for minor keys). */
  scale: Scale;
  name: string;
}

export function key(tonic: Pitch, mode: KeyMode): Key {
  const s = scale(tonic, mode === "major" ? "major" : "natural-minor");
  const tonicName = pitchToString(tonic).replace(/-?\d+$/, "");
  const name = `${tonicName} ${mode}`;
  return { tonic, mode, scale: s, name };
}

/**
 * Compute the scale degree of a pitch within a key (1-7 for diatonic notes).
 * Returns null for chromatic notes that don't sit on a diatonic step.
 *
 * For chromatic notes, use scaleDegreeWithAlteration which returns
 * a (degree, alteration) pair like (5, "♯") for sharp 5.
 */
export function scaleDegreeOf(pitch: Pitch, k: Key): number | null {
  const iv = intervalBetween(k.tonic, pitch);
  // Reduce to within an octave.
  const simpleNumber = ((iv.number - 1) % 7) + 1;
  // Verify the pitch actually appears in the scale (compare semitones modulo 12).
  const expectedSemis = k.scale.intervals[simpleNumber - 1]?.semitones;
  if (expectedSemis === undefined) return null;
  const actualSimpleSemis = ((iv.semitones % 12) + 12) % 12;
  if (actualSimpleSemis === expectedSemis % 12) return simpleNumber;
  return null;
}

export type Alteration = "♭" | "♯" | "♮" | "";

/**
 * Compute scale degree with chromatic alteration.
 * E.g. F# in C major = (4, "♯"). Eb in C major = (3, "♭").
 */
export function scaleDegreeWithAlteration(
  pitch: Pitch,
  k: Key,
): { degree: number; alteration: Alteration } | null {
  const iv = intervalBetween(k.tonic, pitch);
  const simpleNumber = ((iv.number - 1) % 7) + 1;
  const expectedSemis = k.scale.intervals[simpleNumber - 1]?.semitones;
  if (expectedSemis === undefined) return null;
  const actualSimpleSemis = ((iv.semitones % 12) + 12) % 12;
  const expectedSimpleSemis = expectedSemis % 12;
  const diff = actualSimpleSemis - expectedSimpleSemis;
  let alteration: Alteration = "";
  if (diff === 1 || diff === -11) alteration = "♯";
  else if (diff === -1 || diff === 11) alteration = "♭";
  else if (diff !== 0) return null;
  return { degree: simpleNumber, alteration };
}

/**
 * Roman numeral name for a scale degree, respecting key mode.
 *
 * Major key: I ii iii IV V vi vii°
 * Minor key (natural): i ii° III iv v VI VII
 *
 * Note: triad quality is determined by the diatonic chord built on each degree.
 * This function returns the canonical Roman numeral as a label only.
 */
export function diatonicRomanNumeral(degree: number, k: KeyMode): string {
  if (degree < 1 || degree > 7) throw new Error(`Degree ${degree} out of range`);
  const major = ["I", "ii", "iii", "IV", "V", "vi", "vii°"];
  const minor = ["i", "ii°", "III", "iv", "v", "VI", "VII"];
  return (k === "major" ? major : minor)[degree - 1]!;
}

/** All 12 major and 12 minor keys (24 total) — useful for random key selection. */
export function allKeys(): Key[] {
  const tonics: Pitch[] = [
    { letter: "C", accidental: 0, octave: 4 },
    { letter: "G", accidental: 0, octave: 4 },
    { letter: "D", accidental: 0, octave: 4 },
    { letter: "A", accidental: 0, octave: 4 },
    { letter: "E", accidental: 0, octave: 4 },
    { letter: "B", accidental: 0, octave: 4 },
    { letter: "F", accidental: 1, octave: 4 }, // F#
    { letter: "C", accidental: 1, octave: 4 }, // C#
    { letter: "F", accidental: 0, octave: 4 },
    { letter: "B", accidental: -1, octave: 4 }, // Bb
    { letter: "E", accidental: -1, octave: 4 }, // Eb
    { letter: "A", accidental: -1, octave: 4 }, // Ab
    { letter: "D", accidental: -1, octave: 4 }, // Db
  ];
  const keys: Key[] = [];
  for (const t of tonics) {
    keys.push(key(t, "major"));
    keys.push(key(t, "minor"));
  }
  return keys;
}
